import { createContext, FC, useEffect, useMemo, useState } from 'react';

import uniq from 'lodash.uniq';

import { LineType } from './enums/LineType';
import { OrderItem } from './interfaces/Order';
import IOrderDetails from './interfaces/OrderDetails';
import OrderLine from './interfaces/OrderLine';
import OrderLineItemMap from './interfaces/OrderLineMap';
import SalesItemResponse from './interfaces/SalesItemResponse';
import { getSalesItem } from './services/salesItemApi';
import { splitArrayIntoChunks } from './utils/immutability-utils';

interface ISaferHomesStateProps {
  isLoaded: boolean;
  saferHomesText: string | undefined;
}
export interface IOrderDetailsContextProps {
  order: IOrderDetails | null;
  orderItems: OrderItem[] | null;
  saferHomesState: ISaferHomesStateProps;
  setSaferHomesState: React.Dispatch<React.SetStateAction<ISaferHomesStateProps>>;
}

const initialSaferHomesState = {
  isLoaded: false,
  saferHomesText: undefined,
};

export const initialState = {
  order: null,
  orderItems: [],
  saferHomesState: initialSaferHomesState,
  setSaferHomesState: () => {},
};

const OrderDetailsContext = createContext<IOrderDetailsContextProps>(initialState);

const fixFloatingPoint = (num: number): number => +num.toFixed(10);

const aggregateOrderLines = (orderLine: OrderLine, other?: OrderLine): OrderLine => {
  if (!other) {
    return orderLine;
  }
  return {
    ...orderLine,
    amount: fixFloatingPoint(orderLine.amount + other.amount),
    quantity: fixFloatingPoint(+(orderLine.quantity || 0) + (other.quantity || 0)),
  };
};

export function getOrderItemMap(orderLineList: OrderLine[]): OrderLineItemMap {
  return orderLineList.reduce((map: OrderLineItemMap, orderLine) => {
    const itemNo = orderLine.itemNo.replace(/\./g, '');
    return {
      ...map,
      [itemNo]: aggregateOrderLines(orderLine, map[itemNo]),
    };
  }, {});
}

export interface IOrderDetailsContextProviderProps {
  order: IOrderDetails | null;
  children: React.ReactNode;
}

const OrderDetailsContextProvider: FC<IOrderDetailsContextProviderProps> = ({
  order,
  children,
}) => {
  const [orderItems, setOrderItems] = useState<OrderItem[] | null>(null);
  const [saferHomesState, setSaferHomesState] =
    useState<ISaferHomesStateProps>(initialSaferHomesState);
  useEffect(() => {
    let unmounted = false;

    async function fetchSalesItems(orderLines: OrderLine[]) {
      try {
        // Filter out delivery order lines, create an array
        // of item number and make sure the array is unique.
        const itemNumbers = uniq(
          orderLines
            .filter(({ lineType }) => lineType !== LineType.Delivery)
            .map(({ itemNo }) => itemNo),
        );

        // The api can take max 50 items at a time so split them into chunks.
        const itemNumberChunks = splitArrayIntoChunks(50, itemNumbers);

        // Create an array of promises per chunk.
        const salesItemCalls = itemNumberChunks.reduce(
          (promises: Promise<SalesItemResponse>[], itemNumberChunk: string[]) => [
            ...promises,
            getSalesItem(itemNumberChunk),
          ],
          [],
        );

        const response = await Promise.all(salesItemCalls);
        const orderItemMap = getOrderItemMap(orderLines);
        if (!unmounted) {
          setOrderItems(
            response
              .map(({ data }) => data)
              .flat()
              .map((salesItem) => ({
                salesItem,
                orderLine: orderItemMap[salesItem.itemKey.itemNo],
              })),
          );
        }
      } catch (error) {
        console.error(error);
      }
    }

    if (order) {
      fetchSalesItems(order.orderLines);
    }

    return () => {
      unmounted = true;
    };
  }, [order]);

  const contextValue = useMemo(() => {
    return { orderItems, order, saferHomesState, setSaferHomesState };
  }, [order, orderItems, saferHomesState, setSaferHomesState]);

  return (
    <OrderDetailsContext.Provider value={contextValue}>{children}</OrderDetailsContext.Provider>
  );
};

export { OrderDetailsContext, OrderDetailsContextProvider };
